import { Image } from '@perxis-auto/ui-kit/lib/components/Gallery';

import { ISettings, MediaType } from '@/interfaces/settings.interfaces';
import getScrollbarWidth from '@/utils/getScrollbarWidth';

import { ExtendedCarItem } from '../types';

import getCarTitle from './getCarTitle';

type Options = {
  view: 'tile' | 'card';
  media: ISettings['media'];
};

const IMAGE_SIZE = {
  preview: [160, 120],
  xxs: [320, 240],
  xs: [480, 360],
  sm: [640, 480],
  md: [960, 720],
} as const;

/**
 * Получить медиа-условия, описывающие свойства области просмотра
 * @param view Тип области просмотра
 * @returns {Array<string>} Массив медиа-условий, описывающих свойства области просмотра
 */
export const getImageSizes = (view: Options['view']) => {
  const scrollbarWidth = getScrollbarWidth();

  // Значения массива зависят от сетки приложения
  const sizes: Array<string> = [];

  if (view === 'card') {
    sizes.push(
      // Ширина картинки занимает ширину экрана без scrollbar минус левые и правые отступы от контейнера.
      `(max-width: 575px) calc((100vw - ${scrollbarWidth}px) - var(--perxis-auto-container-padding-xxs-inner) * 2)`,
    );
    sizes.push(
      // Ширина картинки занимает ширину экрана без scrollbar минус левые и правые отступы от контейнера.
      `(max-width: 991px) calc((100vw - ${scrollbarWidth}px) - var(--perxis-auto-container-padding-sm-inner) * 2)`,
    );
    sizes.push(
      // Ширина картинки занимает 14 колонок от ширины экрана без scrollbar минус левые и правые отступы от контейнера.
      // В расчетах участвует горизонтальный gutter для правильного расчета ширины колонки, так как ширина Row учитывает отрицательный внешний отступ.
      `(max-width: 1199px) calc((((100vw - ${scrollbarWidth}px) - var(--perxis-auto-container-padding-lg-inner) * 2 + 16px * 2) * 58.333333333333336 / 100) - 16px * 2)`,
    );
    sizes.push(
      // Медиа-запрос покрывает отрезок между xl и максимальной шириной контейнера с учетом горизонтальных отступов.
      // Ширина картинки занимает 14 колонок от ширины экрана без scrollbar минус левые и правые отступы от контейнера.
      // В расчетах участвует горизонтальный gutter для правильного расчета ширины колонки, так как ширина Row учитывает отрицательный внешний отступ.
      `(max-width: calc(var(--perxis-auto-container-max-width-inner) + var(--perxis-auto-container-padding-lg-inner) * 2)) calc((((100vw - ${scrollbarWidth}px) - var(--perxis-auto-container-padding-xl-inner) * 2 + 18px * 2) * 58.333333333333336 / 100) - 18px * 2)`,
    );
    sizes.push(
      // Ширина картинки занимает 14 колонок от максимальной шириной контейнера.
      // В расчетах участвует горизонтальный gutter для правильного расчета ширины колонки, так как ширина Row учитывает отрицательный внешний отступ.
      `calc(((var(--perxis-auto-container-max-width-inner) + 18px * 2) * 58.333333333333336 / 100) - 18px * 2)`,
    );
  }

  if (view === 'tile') {
    sizes.push(
      // Ширина картинки занимает ширину экрана без scrollbar минус левые и правые отступы от списка с авто.
      `(max-width: 575px) calc((100vw - ${scrollbarWidth}px) - var(--perxis-auto-list-col-padding-right-inner) - var(--perxis-auto-list-col-padding-left-inner))`,
    );
    sizes.push(
      // Ширина картинки занимает 12 колонок от ширины экрана без scrollbar минус левые и правые отступы от списка с авто.
      // В расчетах участвует горизонтальный gutter для правильного расчета ширины колонки, так как ширина Row учитывает отрицательный внешний отступ.
      `(max-width: 767px) calc((((100vw - ${scrollbarWidth}px) - var(--perxis-auto-list-col-padding-right-inner) - var(--perxis-auto-list-col-padding-left-inner) + 9px * 2) * 50 / 100) - 9px * 2)`,
    );
    sizes.push(
      // Ширина картинки занимает 12 колонок от ширины экрана без scrollbar минус левые и правые отступы от списка с авто.
      // В расчетах участвует горизонтальный gutter для правильного расчета ширины колонки, так как ширина Row учитывает отрицательный внешний отступ.
      `(max-width: 991px) calc(((100vw - ${scrollbarWidth}px) * 50 / 100) - var(--perxis-auto-list-col-padding-right-inner) - var(--perxis-auto-list-col-padding-left-inner))`,
    );
    sizes.push(
      // Ширина картинки занимает 16 колонок от ширины экрана без scrollbar минус левые и правые отступы от списка с авто, от рассчитанной ширины 12 колонок.
      // В расчетах участвует горизонтальный gutter для правильного расчета ширины колонки, так как ширина Row учитывает отрицательный внешний отступ.
      `(max-width: 1199px) calc(((((100vw - ${scrollbarWidth}px) * 66.66666666666666 / 100) - var(--perxis-auto-list-col-padding-right-inner) - var(--perxis-auto-list-col-padding-left-inner) + 9px * 2) * 50 / 100) - 9px * 2)`,
    );
    sizes.push(
      // Ширина картинки занимает 18 колонок от ширины экрана без scrollbar минус левые и правые отступы от списка с авто, от рассчитанной ширины 8 колонок.
      // В расчетах участвует горизонтальный gutter для правильного расчета ширины колонки, так как ширина Row учитывает отрицательный внешний отступ.
      `(max-width: 1599px) calc(((((100vw - ${scrollbarWidth}px) * 75 / 100) - var(--perxis-auto-list-col-padding-right-inner) - var(--perxis-auto-list-col-padding-left-inner) + 9px * 2) * 33.33333333333333 / 100) - 9px * 2)`,
    );
    sizes.push(
      // Ширина картинки занимает 19 колонок от ширины экрана без scrollbar минус левые и правые отступы от списка с авто, от рассчитанной ширины 6 колонок.
      // В расчетах участвует горизонтальный gutter для правильного расчета ширины колонки, так как ширина Row учитывает отрицательный внешний отступ.
      `calc(((((100vw - ${scrollbarWidth}px) * 79.16666666666666 / 100) - var(--perxis-auto-list-col-padding-right-inner) - var(--perxis-auto-list-col-padding-left-inner) + 9px * 2) * 25 / 100) - 9px * 2)`,
    );
  }

  return sizes;
};

/**
 * Привести `media` к единому формату `Image`
 * @param extendedData объект автомобиля, используется для поиска данных по изображениям
 * @param options опции
 * @returns {Image} Массив свойств для картинок полученных из поля `media`
 */
const transformMedia = (
  extendedData: ExtendedCarItem,
  options: Options,
): Array<Image> => {
  const title = getCarTitle(extendedData);
  const { media: mediaImages = [] } = extendedData.data;
  const { view, media } = options;

  const preparedImages = mediaImages.reduce<Array<Image>>((acc, image) => {
    const srcSet: Array<string> = [];

    if (!image || !image.file) {
      return acc;
    }

    const original = `${media.apiRoot}/${image.file.id}`;

    if (view === 'card') {
      srcSet.push(
        `${original}?p=crop-${IMAGE_SIZE.xxs[0]}-${IMAGE_SIZE.xxs[1]} ${IMAGE_SIZE.xxs[0]}w`,
      );
      srcSet.push(
        `${original}?p=crop-${IMAGE_SIZE.xs[0]}-${IMAGE_SIZE.xs[1]} ${IMAGE_SIZE.xs[0]}w`,
      );
      srcSet.push(
        `${original}?p=crop-${IMAGE_SIZE.sm[0]}-${IMAGE_SIZE.sm[1]} ${IMAGE_SIZE.sm[0]}w`,
      );
      srcSet.push(
        `${original}?p=crop-${IMAGE_SIZE.md[0]}-${IMAGE_SIZE.md[1]} ${IMAGE_SIZE.md[0]}w`,
      );
      srcSet.push(`${original} 1800w`);
    }

    if (view === 'tile') {
      srcSet.push(
        `${original}?p=crop-${IMAGE_SIZE.xxs[0]}-${IMAGE_SIZE.xxs[1]} ${IMAGE_SIZE.xxs[0]}w`,
      );
      srcSet.push(
        `${original}?p=crop-${IMAGE_SIZE.xs[0]}-${IMAGE_SIZE.xs[1]} ${IMAGE_SIZE.xs[0]}w`,
      );
      srcSet.push(
        `${original}?p=crop-${IMAGE_SIZE.sm[0]}-${IMAGE_SIZE.sm[1]} ${IMAGE_SIZE.sm[0]}w`,
      );
    }

    const sizes = getImageSizes(view);
    const thumbnail = `${original}?p=crop-${IMAGE_SIZE.preview[0]}-${IMAGE_SIZE.preview[1]}`;

    return [
      ...acc,
      {
        original,
        thumbnail,
        ...(srcSet.length ? { srcSet: srcSet.join(',') } : {}),
        ...(sizes.length ? { sizes: sizes.join(',') } : {}),
        title,
      },
    ];
  }, []);

  return preparedImages;
};

/**
 * Привести `images` к единому формату `Image`
 * @param extendedData объект автомобиля, используется для поиска данных по изображениям
 * @param options опции
 * @returns {Image} Массив свойств для картинок полученных из поля `images` и `thumbnails`
 */
const transformImages = (
  extendedData: ExtendedCarItem,
  options: Options,
): Array<Image> => {
  const title = getCarTitle(extendedData);
  const { images = [], thumbnails } = extendedData.data;
  const { view } = options;

  const preparedImages = images.map((image, i) => {
    if (!thumbnails) {
      return {
        original: image,
        title,
      };
    }

    const srcSet: Array<string> = [];

    if (view === 'card') {
      if (thumbnails.xxs?.[i]) {
        srcSet.push(`${thumbnails.xxs[i]} ${IMAGE_SIZE.xxs[0]}w`);
      }

      if (thumbnails.xs?.[i]) {
        srcSet.push(`${thumbnails.xs[i]} ${IMAGE_SIZE.xs[0]}w`);
      }

      if (thumbnails.sm?.[i]) {
        srcSet.push(`${thumbnails.sm[i]} ${IMAGE_SIZE.sm[0]}w`);
      }

      if (thumbnails.md?.[i]) {
        srcSet.push(`${thumbnails.md[i]} ${IMAGE_SIZE.md[0]}w`);
      }

      if (srcSet.length) {
        srcSet.push(`${image} 1800w`);
      }
    }

    if (view === 'tile') {
      if (thumbnails.xxs?.[i]) {
        srcSet.push(`${thumbnails.xxs[i]} ${IMAGE_SIZE.xxs[0]}w`);
      }

      if (thumbnails.xs?.[i]) {
        srcSet.push(`${thumbnails.xs[i]} ${IMAGE_SIZE.xs[0]}w`);
      }

      if (thumbnails.sm?.[i]) {
        srcSet.push(`${thumbnails.sm[i]} ${IMAGE_SIZE.sm[0]}w`);
      }
    }

    const thumbnail = thumbnails.preview?.[i];
    const sizes = getImageSizes(view);

    return {
      original: image,
      ...(thumbnail ? { thumbnail } : {}),
      ...(srcSet.length ? { srcSet: srcSet.join(',') } : {}),
      ...(sizes.length ? { sizes: sizes.join(',') } : {}),
      title,
    };
  });

  return preparedImages;
};

/**
 * Формирует массив подготвленных объектов изображений
 * @param extendedData объект автомобиля, используется для поиска данных по изображениям
 * @param options опции
 * @returns {Image} Массив свойств для картинок
 */
const getCarImages = (extendedData: ExtendedCarItem, options: Options): Array<Image> => {
  const { media } = options;

  switch (media.type) {
    case MediaType.Images: {
      return transformImages(extendedData, options);
    }
    case MediaType.Media: {
      return transformMedia(extendedData, options);
    }
    default:
      return [];
  }
};

export default getCarImages;
